/*
 * <BeCountView> / <BeStatusStrip> -- React wrappers for the .be-count-view
 * cell and the .be-status-strip row (the Tracker "N items" window status
 * strip; see components/count-view.css for provenance).
 *
 * The count prop reproduces Tracker's wording ("no items" / "1 item" /
 * "N items"); children override it for the extended, general-purpose cell.
 * typeAhead swaps in the muted match string, busy shows the mini barber pole.
 *
 *   <BeStatusStrip>
 *     <BeCountView count={12} busy />
 *     <BeCountView width="fill">ready.</BeCountView>
 *     <BeScrollBar orientation="horizontal" ... />
 *   </BeStatusStrip>
 */

import type { CSSProperties, ReactNode } from "react";
import { type BeProps, beComponent } from "./create.tsx";

export type BeCountViewWidth = "fixed" | "auto" | "fill";

export type BeCountViewProps = BeProps<
	"div",
	{
		/** Item count, rendered with Tracker's wording; ignored when children are given. */
		count?: number;
		/** Type-ahead match string: replaces the count, muted (Tracker's SetTypeAhead). */
		typeAhead?: string;
		/** Show the mini barber pole (Tracker's StartBarberPole while polling). */
		busy?: boolean;
		/** fixed = Tracker's 63px (default), auto = content-sized, fill = greedy. */
		width?: BeCountViewWidth | number;
		children?: ReactNode;
	}
>;

export const BeCountView = beComponent<BeCountViewProps, HTMLDivElement>("BeCountView", (props, be) => {
	const { count, typeAhead, busy, width = "fixed", scale, className, style, children, ...rest } = props;

	const typingAhead = typeAhead !== undefined && typeAhead.length > 0;
	const text = children ?? (typingAhead ? typeAhead : count === 0 ? "no items" : count === 1 ? "1 item" : `${count ?? 0} items`);

	const root = be(
		"be-count-view",
		"be-text--mini",
		width === "auto" && "be-count-view--auto",
		width === "fill" && "be-count-view--fill",
		typingAhead && "is-typeahead",
		busy && "is-busy",
	);
	const rootStyle: CSSProperties = { ...root.style };
	if (typeof width === "number") rootStyle.width = width;

	return (
		<div role="status" {...rest} {...root} style={rootStyle}>
			{/* inner span: the strip paints its own bevel and face, and
			    .be-pixel-text must not go on a background-painting element. */}
			<span className="be-pixel-text">{text}</span>
		</div>
	);
});

export type BeStatusStripProps = BeProps<"div", { children?: ReactNode }>;

export const BeStatusStrip = beComponent<BeStatusStripProps, HTMLDivElement>("BeStatusStrip", (props, be) => {
	const { scale, className, style, children, ...rest } = props;
	return (
		<div {...rest} {...be("be-status-strip")}>
			{children}
		</div>
	);
});
